import React, { useState, useRef, useEffect, useImperativeHandle } from "react";
import { Input, Dropdown, SuperButton } from "../../elements";
import { S_LANG, webStorage } from "../../utils/webStorage";
import api from "../../utils/api";
import { isBizNull } from "../../utils";
import { connect } from "react-redux";
import { selectUserState } from "../../selectors/application";
import { getCountryOptions, isLoggedIn } from "../../services/siteService";
import { isEmptyObject, isGlobalSite } from "../../utils/siteUtils";
import { addressTypes } from "../../utils/lynkco-constants";
import getSiteInfo from "../../utils/getSiteInfo";
import InputRow from "../../elements/input-row";
const BillingInfoForm = React.forwardRef((props, ref) => {
  const { fields, isDisabled, address, savable = true, onSaveSuccess, user } = props;
  const {
    firstNameLabel,
    firstNameMissingMessage,
    lastNameLabel,
    lastNameMissingMessage,
    streetLabel,
    streetMissingMessage,
    apartmentLabel,
    postalCodeLabel,
    postalCodeMissingMessage,
    postalCodeWarningMessage,
    cityLabel,
    cityMissingMessage,
    countryLabel,
    countryPrivateImportMessage,
    // addressMissingMessage,
    // addressUnsavedMessage,
    ctaName,
    ctaType,
    colorScheme,
    iconName
  } = fields;
  const lang = webStorage.get(S_LANG);
  const [siteInfo, setSiteInfo] = useState({});
  const [firstName, setFirstName] = useState(address?.firstName);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastName, setLastName] = useState(address?.lastName);
  const [lastNameError, setLastNameError] = useState(null);
  const [streetProps, setStreetProps] = useState(null); // This is a hack required for the autocompletion to work
  const [street, setStreet] = useState(address?.street);
  const [streetError, setStreetError] = useState(null);
  const [apartment, setApartment] = useState(address?.apartment);
  const [apartmentError, setApartmentError] = useState(null);
  const [postalCode, setPostalCode] = useState(address?.postalCode);
  const [postalCodeError, setPostalCodeError] = useState(null);
  const [postalCodeIsValid, setPostalCodeIsValid] = useState(true);
  const [city, setCity] = useState(address?.city);
  const [cityError, setCityError] = useState(null);
  const [country, setCountry] = useState(null);
  const [countryError, setCountryError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [countryPrivateImportMsgShow, setCountryPrivateImportMsgShow] = useState(false);
  const rootEl = useRef();

  useEffect(() => {
    init();
  }, []);
  useEffect(() => {
    if (!isEmptyObject(address)) {
      setFirstName(address.firstName);
      setLastName(address.lastName);
      setStreet(address.street);
      setApartment(address.apartment);
      setPostalCode(address.postalCode);
      setCity(address.city);
    }
  }, [address]);
  useEffect(() => {
    if (address?.countryCode !== siteInfo.country) {
      setCountryPrivateImportMsgShow(true);
    } else {
      setCountryPrivateImportMsgShow(false);
    }
  }, [address, siteInfo]);
  useEffect(() => {
    if (!isEmptyObject(address) && !isEmptyObject(siteInfo) && countries.length > 0) {
      let country;
      if (address.countryCode) {
        country = countries.find(x => x.data.regionAbbr === address?.countryCode);
      } else if (!isGlobalSite()) {
        country = countries.find(c => c.code === siteInfo.country);
      }
      setCountry(country);
    }
  }, [address, countries, siteInfo]);

  useImperativeHandle(ref, () => ({
    setError(error) {
      setFormError(error);
    },
    validateForm() {
      return {
        isValid: validateFields(),
        formData: {
          firstName,
          lastName,
          street,
          apartment,
          postalCode,
          city,
          country: country?.data.regionName,
          countryCode: country?.data.regionAbbr,
          countryId: country?.data.regionId,
          unSave: true
        }
      };
    },
    getEl() {
      return rootEl.current;
    }
  }));
  const init = async () => {
    const siteInfo = await getSiteInfo();
    setSiteInfo(siteInfo);
    const countries = await getCountryOptions();
    setCountries(countries);
  };
  const validateFields = () => {
    let valid = true;
    if (isBizNull(firstName)) {
      setFirstNameError(firstNameMissingMessage.value);
      valid = false;
    }
    if (isBizNull(lastName)) {
      setLastNameError(lastNameMissingMessage.value);
      valid = false;
    }
    if (isBizNull(street)) {
      setStreetError(streetMissingMessage.value);
      valid = false;
    }
    if (isBizNull(postalCode)) {
      setPostalCodeError(postalCodeMissingMessage.value);
      valid = false;
    } else if (!postalCodeIsValid) {
      setPostalCodeError(postalCodeWarningMessage.value);
      valid = false;
    }
    if (isBizNull(city)) {
      setCityError(cityMissingMessage.value);
      valid = false;
    }
    return valid;
  };

  const onAutocompleteLocation = address => {
    if (address.street) setStreet(address.street);
    setCity(address.city ? address.city : "");
    setPostalCode(address.postalCode ? address.postalCode : "");

    if (address.country && countries) {
      const filteredCountry = countries.filter(c => c.data.regionAbbr === address.country);
      if (filteredCountry && filteredCountry.length) {
        setCountry(filteredCountry[0]);
      }
    }
  };

  useEffect(() => {
    if (countries.length) {
      setStreetProps({
        label: streetLabel.value,
        value: street,
        showError: streetError !== null,
        warningMessage: streetError,
        disabled: isDisabled,
        location: { onAutocomplete: onAutocompleteLocation },
        onFocus: evt => {
          setStreetError(null);
        },
        onBlur: evt => {
          setStreet(evt.target.value);
        }
      });
    }
  }, [countries, street]);

  const firstNameProps = {
    label: firstNameLabel.value,
    value: firstName,
    maxLength: 25,
    showError: firstNameError !== null,
    warningMessage: firstNameError,
    disabled: isDisabled,
    onFocus: evt => {
      setFirstNameError(null);
    },
    onBlur: evt => {
      setFirstName(evt.target.value);
    }
  };
  const lastNameProps = {
    label: lastNameLabel.value,
    value: lastName,
    maxLength: 25,
    showError: lastNameError !== null,
    warningMessage: lastNameError,
    disabled: isDisabled,
    onFocus: evt => {
      setLastNameError(null);
    },
    onBlur: evt => {
      setLastName(evt.target.value);
    }
  };

  const apartmentProps = {
    label: apartmentLabel.value,
    value: apartment,
    showError: apartmentError !== null,
    warningMessage: apartmentError,
    disabled: isDisabled,
    onFocus: evt => {
      setApartmentError(null);
    },
    onBlur: evt => {
      setApartment(evt.target.value);
    }
  };
  const postalCodeProps = {
    label: postalCodeLabel.value,
    value: postalCode,
    showError: postalCodeError !== null,
    warningMessage: postalCodeError,
    disabled: isDisabled,
    onFocus: evt => {
      setPostalCodeError(null);
    },
    onBlur: (evt, isValid) => {
      setPostalCode(evt.target.value);
      setPostalCodeIsValid(isValid);
    },
    className: "right-gap"
  };
  const cityProps = {
    label: cityLabel.value,
    value: city,
    showError: cityError !== null,
    warningMessage: cityError,
    disabled: isDisabled,
    onFocus: evt => {
      setCityError(null);
    },
    onBlur: evt => {
      setCity(evt.target.value);
    }
  };
  const countryProps = {
    placeholder: countryLabel.value,
    options: countries,
    selectedOption: country,
    showError: countryError !== null,
    warningMessage: countryError,
    disabled: isDisabled,
    onChange: option => {
      setCountry(option);
      setCountryError(null);
      if (!isGlobalSite()) {
        if (option.code !== siteInfo.country) {
          setCountryPrivateImportMsgShow(true);
        } else {
          setCountryPrivateImportMsgShow(false);
        }
      } else {
        setCountryPrivateImportMsgShow(false);
      }
    }
  };
  const btnProps = ctaName && {
    buttonText: ctaName.value,
    ctaType,
    colorScheme,
    iconName,
    isDisabled: submitting,
    callback: async () => {
      setFormError(null);
      if (validateFields()) {
        setSubmitting(true);
        console.log("country", country);
        let action = "create";
        const body = {
          firstName: user?.firstName,
          lastName: user?.lastName,
          city: city,
          street: street,
          apartment: apartment,
          postalCode: postalCode,
          countryCode: country.data.regionAbbr,
          countryId: country.data.regionId,
          country: country.data.regionName,
          defaultFlag: "0",
          addressType: addressTypes.shipping
        };
        if (address?.addressId) {
          action = "update";
          body.addressId = address.addressId;
          body.defaultFlag = address.defaultFlag;
        }
        await api.address[action](null, body);
        if (onSaveSuccess) {
          if (address?.addressId) {
            Object.assign(address, {
              ...body,
              firstName: firstName,
              lastName: lastName
            });
            onSaveSuccess(address);
          } else {
            onSaveSuccess({
              ...body,
              firstName: firstName,
              lastName: lastName
            });
          }
        }
      }
    }
  };

  return (
    <form className="billing-info-form" ref={rootEl}>
      <Input name="first-name" autocomplete="first-name" {...firstNameProps} />
      <Input name="family-name" autocomplete="family-name" {...lastNameProps} />
      <Input name="address-line1" autocomplete="address-line1" {...streetProps} />
      <Input name="address-line2" autocomplete="address-line2" {...apartmentProps} />
      <InputRow>
        <Input name="postal-code" autocomplete="postal-code" type={/^nl-nl$/i.test(lang) ? "postalCode" : null} {...postalCodeProps} />
        <Input name="address-level2" autocomplete="address-level2" {...cityProps} />
      </InputRow>
      <Dropdown {...countryProps} />
      {/*{countryPrivateImportMsgShow && <div className="private-import-message">{countryPrivateImportMessage.value}</div>}*/}
      {formError && <div className="failed-message">{formError}</div>}
      {isLoggedIn() && savable && <SuperButton {...btnProps} />}
    </form>
  );
});
const mapStateToProps = state => ({
  user: selectUserState(state)
});
BillingInfoForm.displayName = "ShippingInfoForm";
export default connect(mapStateToProps, null, null, { forwardRef: true })(BillingInfoForm);
